import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import {
  DashboardStats,
  DentalOfficeAppointmentWithDetails,
  DentalOfficePatient,
  InvoiceStats,
} from '@/types';

export function useDashboardStats() {
  const { token } = useAuth();

  return useQuery<DashboardStats>({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      // Fetch multiple stats in parallel
      const [patientsRes, appointmentsRes, invoicesRes, revenueRes] = await Promise.allSettled([
        api.get<{ meta: { total: number } }>('/patients?limit=1', token || undefined),
        api.get<{ data: DentalOfficeAppointmentWithDetails[] }>('/appointments/today', token || undefined),
        api.get<{ data: any[] }>('/invoices?status=sent&status=pending', token || undefined),
        api.get<{ data: InvoiceStats }>('/invoices/stats?period=month', token || undefined),
      ]);

      return {
        totalPatients:
          patientsRes.status === 'fulfilled' ? patientsRes.value.meta?.total || 0 : 0,
        todayAppointments:
          appointmentsRes.status === 'fulfilled' ? appointmentsRes.value.data.length : 0,
        pendingInvoices:
          invoicesRes.status === 'fulfilled' ? invoicesRes.value.data.length : 0,
        monthlyRevenue:
          revenueRes.status === 'fulfilled' ? revenueRes.value.data.total_revenue || 0 : 0,
      };
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

export function useTodayAppointments() {
  const { token } = useAuth();

  return useQuery<DentalOfficeAppointmentWithDetails[]>({
    queryKey: ['appointments', 'today'],
    queryFn: async () => {
      const response = await api.get<{ data: DentalOfficeAppointmentWithDetails[] }>(
        '/appointments/today',
        token || undefined
      );
      return response.data;
    },
    enabled: !!token,
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    refetchOnWindowFocus: true,
  });
}

export function useRecentPatients() {
  const { token } = useAuth();

  return useQuery<DentalOfficePatient[]>({
    queryKey: ['patients', 'recent'],
    queryFn: async () => {
      const response = await api.get<{ data: DentalOfficePatient[] }>(
        '/patients?limit=5&sort=-last_visit_date',
        token || undefined
      );
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useInvoiceStats() {
  const { token } = useAuth();

  return useQuery<InvoiceStats>({
    queryKey: ['invoices', 'stats'],
    queryFn: async () => {
      const response = await api.get<{ data: InvoiceStats }>(
        '/invoices/stats?period=month',
        token || undefined
      );
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
